@if (!tutorial) {
    <!-- choosing a tutorial -->
    <div class="list-view">
        <header class="list-header">
            <h2>Tutorials</h2>
            <button mat-icon-button (click)="showHowItWorks = !showHowItWorks" [attr.aria-expanded]="showHowItWorks" matTooltip="How the tutorials work" aria-label="How the tutorials work">
                <mat-icon>help_outline</mat-icon>
            </button>
        </header>
        <p class="lead">Short video lessons on modelling, with the canvas to the left to practise on as you go.</p>

        @if (showHowItWorks) {
            <section class="how-it-works" aria-label="How the tutorials work">
                <ul>
                    <li>Each step has a video and what to do. Follow along by building the model on the canvas.</li>
                    <li>Use <b>Next</b> and <b>Previous</b> to move between steps. If your model goes astray, <b>Reset the canvas to this step</b>, under the video, puts it back to how it should look.</li>
                    <li>Nothing on this canvas is saved to your organisation.</li>
                </ul>
                <button mat-button color="accent" (click)="gotIt()">Got it</button>
            </section>
        }

        @if (loading) {
            <div class="status" role="status"><mat-spinner diameter="22"></mat-spinner><span>Loading tutorials…</span></div>
        }@else if (loadFailed) {
            <p class="error" role="alert">The tutorials couldn't be loaded.</p>
            <button mat-stroked-button color="accent" (click)="getTutorials()">Try again</button>
        }@else if (!tutorials?.length) {
            <p class="status">There are no tutorials available right now.</p>
        }

        <div class="tutorial-list">
            @for (t of tutorials; track t.type; let i = $index) {
                @let saved = progressFor(t);
                <button type="button" class="tutorial-card" (click)="open(t)" [attr.data-testid]="'tutorial-' + t.type">
                    <span class="card-number">{{i + 1}}</span>
                    <span class="card-body">
                        <span class="card-title">{{t.name}}</span>
                        <span class="card-text" [innerHTML]="introduction(t)"></span>
                        <span class="card-meta">
                            {{t.steps.length}} steps
                            @if (saved?.done) {
                                <span class="done"><mat-icon>check_circle</mat-icon>Done</span>
                            }@else if (saved && saved.step > 1) {
                                <span class="in-progress">· Step {{saved.step}} of {{t.steps.length}}</span>
                            }
                        </span>
                    </span>
                    <span class="card-cta">{{saved?.done ? 'Again' : (saved && saved.step > 1 ? 'Continue' : 'Start')}}<mat-icon>arrow_forward</mat-icon></span>
                </button>
            }
        </div>
    </div>
}@else {
    <!-- following a tutorial -->
    <div class="step-view">
        <header class="step-header">
            <button mat-button class="back" (click)="backToList()"><mat-icon>arrow_back</mat-icon>All tutorials</button>
            <div class="eyebrow">{{tutorial.name}} · Step {{step}} of {{stepCount}}</div>
            <ol class="steps" aria-label="The tutorial's steps">
                @for (s of steps; track $index; let i = $index) {
                    <li>
                        <button type="button" [class.done]="i + 1 < step" [class.current]="i + 1 === step" [attr.aria-current]="i + 1 === step ? 'step' : null"
                            [matTooltip]="(i + 1) + '. ' + stepName(s)" [attr.aria-label]="'Step ' + (i + 1) + ': ' + stepName(s)" (click)="goTo(i + 1)"></button>
                    </li>
                }
            </ol>
            <h2>{{title}}</h2>
        </header>

        <div class="step-body">
            <app-video [videoUrl]="videoUrl" [startPlaying]="true" [showRestart]="false"></app-video>

            <!-- the canvas is the user's own as they follow along; this puts it back to how the step starts -->
            <div class="reset-row">
                <span>Canvas doesn't match the video?</span>
                <button mat-stroked-button color="accent" (click)="goTo(step, true)" matTooltip="Replace the model on the canvas with how it looks at the start of this step" data-testid="tutorial-reset-canvas">
                    <mat-icon>sync</mat-icon>Reset the canvas to this step
                </button>
            </div>

            @if (stepSummary) {
                <div class="summary" [innerHTML]="stepSummary"></div>
            }
            @if (stepScript) {
                <section class="script" aria-labelledby="script-title">
                    <button type="button" class="script-toggle" id="script-title" (click)="showScript = !showScript" [attr.aria-expanded]="showScript">
                        <mat-icon>{{showScript ? 'expand_less' : 'expand_more'}}</mat-icon>What to do in this step
                    </button>
                    @if (showScript) {
                        <div class="script-text" [innerHTML]="stepScript"></div>
                    }
                </section>
            }
        </div>

        <footer class="step-footer">
            <button mat-stroked-button (click)="previousStep()" [disabled]="step <= 1"><mat-icon>arrow_back</mat-icon>Previous</button>
            <span class="spacer"></span>
            <button mat-icon-button (click)="restart()" matTooltip="Restart the tutorial, from step 1 with its canvas" aria-label="Restart the tutorial" data-testid="tutorial-restart"><mat-icon>restart_alt</mat-icon></button>
            @if (step < stepCount) {
                <button mat-flat-button color="accent" (click)="nextStep()" data-testid="tutorial-next">Next step<mat-icon iconPositionEnd>arrow_forward</mat-icon></button>
            }@else {
                <button mat-flat-button color="accent" (click)="finish()" data-testid="tutorial-finish"><mat-icon>check</mat-icon>Finish</button>
            }
        </footer>
    </div>
}
